/**
 * PulseDashboard
 * Main container assembling all Platform Pulse sections:
 *   1. TickerStrip
 *   2. PlatformKpiCards
 *   3. Timeseries charts (conversations line chart + users area chart)
 *   4. TrendingAgentsBoard
 *   5. TopicIntelligencePanel
 *   6. ActivityHeatmap
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { activeLocale } from '@/utils/locale';
import { useLanguage } from '@/hooks/useLanguage';
import { Select } from 'antd';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { PlatformKpiCards } from './PlatformKpiCards';
import { TrendingAgentsBoard } from './TrendingAgentsBoard';
import { AgentSummaryTable } from './AgentSummaryTable';
import { ActivityHeatmap } from './ActivityHeatmap';
import { TickerStrip } from './TickerStrip';
import { TopicIntelligencePanel } from './TopicIntelligencePanel';
import { ChartSection } from './ChartSection';
import { useChartTheme, tooltipStyle as makeTooltipStyle, axisTickStyle, gridProps, PALETTE } from '../../utils/chartTheme';
import {
  renderAgentOption,
  renderTenantOption,
  AgentPlaceholder,
  TenantPlaceholder,
  AGENT_SELECT_STYLE,
  TENANT_SELECT_STYLE,
  AGENT_DROPDOWN_STYLE,
  TENANT_DROPDOWN_STYLE,
} from '../../utils/selectRenderers';
import type { AgentStat, TimeseriesPoint, TenantRanking, TenantNode, TopicsOverview, InsightFilters, InsightTemplate } from '../../types/insights';

const BLUE = '#3b82f6';

// ─── Date formatting ──────────────────────────────────────────────────────────

function formatDateLabel(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString(activeLocale(), { month: 'short', day: 'numeric' });
}

// ─── Timeseries chart helpers ─────────────────────────────────────────────────

interface ChartDataPoint {
  date: string;
  [agentName: string]: string | number;
}

function buildAgentConversationData(
  agentTimeseries: TimeseriesPoint[],
  top5Ids: string[]
): ChartDataPoint[] {
  // Collect all unique dates
  const dateSet = new Set<string>();
  for (const pt of agentTimeseries) {
    if (top5Ids.includes(pt.chatbot_id ?? '')) dateSet.add(pt.date);
  }
  const dates = Array.from(dateSet).sort();

  // Build map for quick lookup
  const lookup = new Map<string, Map<string, number>>();
  for (const pt of agentTimeseries) {
    if (!pt.chatbot_id || !pt.chatbot_name) continue;
    if (!top5Ids.includes(pt.chatbot_id)) continue;
    if (!lookup.has(pt.date)) lookup.set(pt.date, new Map());
    lookup.get(pt.date)!.set(pt.chatbot_name, pt.conversations);
  }

  // Get agent names for top5
  const top5Names = new Map<string, string>();
  for (const pt of agentTimeseries) {
    if (pt.chatbot_id && top5Ids.includes(pt.chatbot_id) && pt.chatbot_name) {
      top5Names.set(pt.chatbot_id, pt.chatbot_name);
    }
  }

  return dates.map(date => {
    const dayMap = lookup.get(date) ?? new Map();
    const point: ChartDataPoint = { date: formatDateLabel(date) };
    for (const name of top5Names.values()) {
      point[name] = dayMap.get(name) ?? 0;
    }
    return point;
  });
}

function buildTotalUsersData(tenantTimeseries: TimeseriesPoint[]): { date: string; users: number }[] {
  // Sum users across all tenants per date (users are tenant-scoped, no double-count)
  const byDate = new Map<string, number>();
  for (const pt of tenantTimeseries) {
    if (!pt.date) continue;
    byDate.set(pt.date, (byDate.get(pt.date) ?? 0) + (pt.users ?? 0));
  }
  return Array.from(byDate.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, users]) => ({ date: formatDateLabel(date), users }));
}

function buildTenantConversationData(
  tenantTimeseries: TimeseriesPoint[],
  topTenantIds: string[]
): ChartDataPoint[] {
  const dateSet = new Set<string>();
  for (const pt of tenantTimeseries) {
    if (topTenantIds.includes(pt.tenant_id ?? '')) dateSet.add(pt.date);
  }
  const dates = Array.from(dateSet).sort();

  const lookup = new Map<string, Map<string, number>>();
  const tenantNames = new Map<string, string>();
  for (const pt of tenantTimeseries) {
    if (!pt.tenant_id || !topTenantIds.includes(pt.tenant_id)) continue;
    if (!lookup.has(pt.date)) lookup.set(pt.date, new Map());
    lookup.get(pt.date)!.set(pt.tenant_name ?? pt.tenant_id, pt.conversations);
    if (pt.tenant_name) tenantNames.set(pt.tenant_id, pt.tenant_name);
  }

  return dates.map(date => {
    const dayMap = lookup.get(date) ?? new Map();
    const point: ChartDataPoint = { date: formatDateLabel(date) };
    for (const name of tenantNames.values()) {
      point[name] = dayMap.get(name) ?? 0;
    }
    return point;
  });
}

function buildTenantUsersData(
  tenantTimeseries: TimeseriesPoint[],
  topTenantIds: string[]
): ChartDataPoint[] {
  const dateSet = new Set<string>();
  for (const pt of tenantTimeseries) {
    if (topTenantIds.includes(pt.tenant_id ?? '')) dateSet.add(pt.date);
  }
  const dates = Array.from(dateSet).sort();

  const lookup = new Map<string, Map<string, number>>();
  const tenantNames = new Map<string, string>();
  for (const pt of tenantTimeseries) {
    if (!pt.tenant_id || !topTenantIds.includes(pt.tenant_id)) continue;
    if (!lookup.has(pt.date)) lookup.set(pt.date, new Map());
    lookup.get(pt.date)!.set(pt.tenant_name ?? pt.tenant_id, pt.users ?? 0);
    if (pt.tenant_name) tenantNames.set(pt.tenant_id, pt.tenant_name);
  }

  return dates.map(date => {
    const dayMap = lookup.get(date) ?? new Map();
    const point: ChartDataPoint = { date: formatDateLabel(date) };
    for (const name of tenantNames.values()) {
      point[name] = dayMap.get(name) ?? 0;
    }
    return point;
  });
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PulseDashboardProps {
  agentStats: AgentStat[];
  agentTimeseries: TimeseriesPoint[];
  globalAgentStats: AgentStat[];
  globalAgentTimeseries: TimeseriesPoint[];
  globalAgentLoading: boolean;
  tenantRankings: TenantRanking[];
  tenantTimeseries: TimeseriesPoint[];
  chatbots: { chatbot_id: string; chatbot_name: string; tenant_id: string }[];
  tenantTree: TenantNode[];
  topicsOverview: TopicsOverview | null;
  topicsOverviewLoading: boolean;
  filters: InsightFilters;
  loading: boolean;
  templates: InsightTemplate[];
  onTopicFilterChange: (chatbotIds: string[], tenantIds: string[], topN: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PulseDashboard({
  agentStats,
  agentTimeseries,
  globalAgentStats,
  globalAgentTimeseries,
  globalAgentLoading,
  tenantRankings,
  tenantTimeseries,
  chatbots,
  tenantTree,
  topicsOverview,
  topicsOverviewLoading,
  filters,
  loading,
  templates,
  onTopicFilterChange,
}: PulseDashboardProps) {
  const { t } = useLanguage();
  // Top 5 agents by conversation count
  const top5Agents = useMemo(
    () => [...agentStats].sort((a, b) => b.total_conversations - a.total_conversations).slice(0, 5),
    [agentStats]
  );
  const top5Ids = useMemo(() => top5Agents.map(a => a.chatbot_id), [top5Agents]);

  // Agent selector for conversations chart (empty = top 5 auto)
  const [selectedChartAgents, setSelectedChartAgents] = useState<string[]>([]);
  const activeAgentIds = selectedChartAgents.length > 0 ? selectedChartAgents : top5Ids;

  // Tenant chart selector (empty = top 5 auto)
  const [selectedChartTenants, setSelectedChartTenants] = useState<string[]>([]);

  // Reset chart selectors and legend state when global scope changes
  useEffect(() => {
    setSelectedChartAgents([]);
    setSelectedChartTenants([]);
    setHiddenAgents(new Set());
    setHiddenTenants(new Set());
  }, [agentStats]);

  const agentConversationData = useMemo(
    () => buildAgentConversationData(agentTimeseries, activeAgentIds),
    [agentTimeseries, activeAgentIds]
  );

  const totalUsersData = useMemo(
    () => buildTotalUsersData(tenantTimeseries),
    [tenantTimeseries]
  );

  // Top 5 tenants by conversations for per-tenant charts
  const top5TenantIds = useMemo(
    () => [...tenantRankings].sort((a, b) => b.total_conversations - a.total_conversations).slice(0, 5).map(t => t.tenant_id),
    [tenantRankings]
  );
  const activeTenantIds = selectedChartTenants.length > 0 ? selectedChartTenants : top5TenantIds;

  const tenantConversationData = useMemo(
    () => buildTenantConversationData(tenantTimeseries, activeTenantIds),
    [tenantTimeseries, activeTenantIds]
  );

  const tenantUsersData = useMemo(
    () => buildTenantUsersData(tenantTimeseries, activeTenantIds),
    [tenantTimeseries, activeTenantIds]
  );

  const tenantNameList = useMemo(() => {
    const names = new Set<string>();
    for (const pt of tenantTimeseries) {
      if (pt.tenant_name && activeTenantIds.includes(pt.tenant_id ?? '')) names.add(pt.tenant_name);
    }
    return Array.from(names);
  }, [tenantTimeseries, activeTenantIds]);

  // All tenant options for the selector
  const allTenantOptions = useMemo(
    () => [...tenantRankings]
      .sort((a, b) => b.total_conversations - a.total_conversations)
      .map(t => ({ value: t.tenant_id, label: t.tenant_name })),
    [tenantRankings]
  );

  // Period in days for ActivityHeatmap
  const periodDays = useMemo(() => {
    const from = new Date(filters.dateFrom);
    const to = new Date(filters.dateTo);
    return Math.max(7, Math.round((to.getTime() - from.getTime()) / 86400000) + 1);
  }, [filters.dateFrom, filters.dateTo]);

  // Derive agent name list for chart lines (from active selection)
  const agentNameList = useMemo(() => {
    const ids = new Set(activeAgentIds);
    return agentStats
      .filter(a => ids.has(a.chatbot_id))
      .sort((a, b) => b.total_conversations - a.total_conversations)
      .map(a => a.chatbot_name);
  }, [agentStats, activeAgentIds]);

  // All agent options for the selector
  const allAgentOptions = useMemo(
    () => [...agentStats]
      .sort((a, b) => b.total_conversations - a.total_conversations)
      .map(a => ({ value: a.chatbot_id, label: a.chatbot_name })),
    [agentStats]
  );

  // Legend click-to-hide: track hidden series per chart group
  const [hiddenAgents, setHiddenAgents] = useState<Set<string>>(new Set());
  const [hiddenTenants, setHiddenTenants] = useState<Set<string>>(new Set());

  const toggleHidden = useCallback((setFn: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) => {
    setFn(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleAgentLegendClick = useCallback((e: any) => {
    toggleHidden(setHiddenAgents, e.dataKey ?? e.value);
  }, [toggleHidden]);

  const handleTenantLegendClick = useCallback((e: any) => {
    toggleHidden(setHiddenTenants, e.dataKey ?? e.value);
  }, [toggleHidden]);

  const ct = useChartTheme();

  // Styled legend with strikethrough on hidden items
  const legendStyle = { fontSize: 11, paddingTop: 12, cursor: 'pointer' };

  const chartAxisStyle = axisTickStyle(ct);
  const chartGridStyle = gridProps(ct);
  const tooltipContentStyle = makeTooltipStyle(ct);

  // ── Reusable render helpers for normal / fullscreen charts ──────────────

  const renderConversationsChart = (height: number) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={agentConversationData} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
        <CartesianGrid {...chartGridStyle} />
        <XAxis dataKey="date" tick={chartAxisStyle} tickLine={false} axisLine={{ stroke: ct.xAxisLine }} />
        <YAxis tick={chartAxisStyle} tickLine={false} axisLine={false} />
        <Tooltip contentStyle={tooltipContentStyle} />
        <Legend
          wrapperStyle={legendStyle}
          onClick={handleAgentLegendClick}
          formatter={(value: string) => (
            <span style={{ color: hiddenAgents.has(value) ? ct.legendHidden : undefined, textDecoration: hiddenAgents.has(value) ? 'line-through' : undefined }}>
              {value}
            </span>
          )}
        />
        {agentNameList.map((name, idx) => (
          <Line key={name} type="monotone" dataKey={name} stroke={PALETTE[idx % PALETTE.length]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} hide={hiddenAgents.has(name)} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );

  const renderUsersChart = (height: number) => (
    <>
      <svg width={0} height={0}>
        <defs>
          <linearGradient id="usersGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
          </linearGradient>
        </defs>
      </svg>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={totalUsersData} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid {...chartGridStyle} />
          <XAxis dataKey="date" tick={chartAxisStyle} tickLine={false} axisLine={{ stroke: ct.xAxisLine }} />
          <YAxis tick={chartAxisStyle} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={tooltipContentStyle} />
          <Area type="monotone" dataKey="users" stroke="#10b981" strokeWidth={2} fill="url(#usersGrad)" fillOpacity={1} dot={false} activeDot={{ r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </>
  );

  const renderTenantConversationsChart = (height: number) => (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={tenantConversationData} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
        <CartesianGrid {...chartGridStyle} />
        <XAxis dataKey="date" tick={chartAxisStyle} tickLine={false} axisLine={{ stroke: ct.xAxisLine }} />
        <YAxis tick={chartAxisStyle} tickLine={false} axisLine={false} />
        <Tooltip contentStyle={tooltipContentStyle} />
        <Legend
          wrapperStyle={legendStyle}
          onClick={handleTenantLegendClick}
          formatter={(value: string) => (
            <span style={{ color: hiddenTenants.has(value) ? ct.legendHidden : undefined, textDecoration: hiddenTenants.has(value) ? 'line-through' : undefined }}>
              {value}
            </span>
          )}
        />
        {tenantNameList.map((name, idx) => (
          <Area key={name} type="monotone" dataKey={name} stroke={PALETTE[idx % PALETTE.length]} fill={PALETTE[idx % PALETTE.length]} fillOpacity={0.12} strokeWidth={2} dot={false} activeDot={{ r: 4 }} hide={hiddenTenants.has(name)} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );

  const renderTenantUsersChart = (height: number) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={tenantUsersData} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
        <CartesianGrid {...chartGridStyle} />
        <XAxis dataKey="date" tick={chartAxisStyle} tickLine={false} axisLine={{ stroke: ct.xAxisLine }} />
        <YAxis tick={chartAxisStyle} tickLine={false} axisLine={false} />
        <Tooltip contentStyle={tooltipContentStyle} />
        <Legend
          wrapperStyle={legendStyle}
          onClick={handleTenantLegendClick}
          formatter={(value: string) => (
            <span style={{ color: hiddenTenants.has(value) ? ct.legendHidden : undefined, textDecoration: hiddenTenants.has(value) ? 'line-through' : undefined }}>
              {value}
            </span>
          )}
        />
        {tenantNameList.map((name, idx) => (
          <Line key={name} type="monotone" dataKey={name} stroke={PALETTE[idx % PALETTE.length]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} hide={hiddenTenants.has(name)} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );

  // ── Tenant chart selector (shared between both tenant charts) ──────────

  const tenantChartSelector = (
    <Select
      mode="multiple"
      size="small"
      placeholder={<TenantPlaceholder text={t('insights.pulse.top5Tenants')} />}
      value={selectedChartTenants}
      onChange={setSelectedChartTenants}
      options={allTenantOptions}
      maxTagCount={2}
      allowClear
      showSearch
      optionFilterProp="label"
      style={TENANT_SELECT_STYLE}
      popupMatchSelectWidth={false}
      dropdownStyle={TENANT_DROPDOWN_STYLE}
      optionRender={(option) => renderTenantOption(option, tenantTree)}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* 1. Ticker strip */}
      <TickerStrip
        agentStats={agentStats}
        agentTimeseries={agentTimeseries}
        tenantRankings={tenantRankings}
        tenantTimeseries={tenantTimeseries}
        loading={loading}
      />

      {/* 2. KPI cards */}
      <PlatformKpiCards
        agentStats={agentStats}
        agentTimeseries={agentTimeseries}
        tenantRankings={tenantRankings}
        tenantTimeseries={tenantTimeseries}
        loading={loading}
      />

      {/* 3. Timeseries charts side by side */}
      {!loading && agentConversationData.length > 0 && (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <ChartSection
            title={t('insights.pulse.conversationsOverTime')}
            subtitle={t('insights.pulse.conversationsOverTimeSub')}
            color={BLUE}
            style={{ flex: '1 1 400px' }}
            headerExtra={
              <Select
                mode="multiple"
                size="small"
                placeholder={<AgentPlaceholder text={t('insights.pulse.top5Agents')} />}
                value={selectedChartAgents}
                onChange={setSelectedChartAgents}
                options={allAgentOptions}
                maxTagCount={2}
                allowClear
                showSearch
                optionFilterProp="label"
                style={AGENT_SELECT_STYLE}
                popupMatchSelectWidth={false}
                dropdownStyle={AGENT_DROPDOWN_STYLE}
                optionRender={renderAgentOption}
              />
            }
            fullscreenContent={renderConversationsChart(480)}
          >
            {renderConversationsChart(240)}
          </ChartSection>

          <ChartSection
            title={t('insights.pulse.activeUsers')}
            subtitle={t('insights.pulse.activeUsersSub')}
            color="#10b981"
            style={{ flex: '1 1 400px' }}
            fullscreenContent={renderUsersChart(480)}
          >
            {renderUsersChart(240)}
          </ChartSection>
        </div>
      )}

      {/* 4. Agent performance — global */}
      <ChartSection
        title={t('insights.pulse.agentPerformance')}
        subtitle={t('insights.pulse.agentPerformanceSub')}
        color="#3b82f6"
      >
        <AgentSummaryTable
          agentStats={globalAgentStats}
          agentTimeseries={globalAgentTimeseries}
          loading={globalAgentLoading}
        />
      </ChartSection>

      {/* 5. Agent usage by tenant */}
      <ChartSection
        title={t('insights.pulse.agentUsageByTenant')}
        subtitle={t('insights.pulse.agentUsageByTenantSub')}
        color="#f59e0b"
      >
        <TrendingAgentsBoard
          agentStats={agentStats}
          agentTimeseries={agentTimeseries}
          tenantTree={tenantTree}
          loading={loading}
        />
      </ChartSection>

      {/* 6. Topic intelligence */}
      <ChartSection
        title={t('insights.pulse.topicIntelligence')}
        subtitle={t('insights.pulse.topicIntelligenceSub')}
        color="#8b5cf6"
        fullscreenWidth={1200}
      >
        <TopicIntelligencePanel
          topicsOverview={topicsOverview}
          loading={topicsOverviewLoading}
          chatbots={chatbots}
          tenantTree={tenantTree}
          filters={filters}
          templates={templates}
          onFilterChange={onTopicFilterChange}
        />
      </ChartSection>

      {/* 7. Per-tenant charts */}
      {!loading && tenantConversationData.length > 0 && (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <ChartSection
            title={t('insights.pulse.tenantConversations')}
            subtitle={t('insights.pulse.tenantConversationsSub')}
            color="#8b5cf6"
            style={{ flex: '1 1 400px' }}
            headerExtra={tenantChartSelector}
            fullscreenContent={renderTenantConversationsChart(480)}
          >
            {renderTenantConversationsChart(240)}
          </ChartSection>

          <ChartSection
            title={t('insights.pulse.tenantActiveUsers')}
            subtitle={t('insights.pulse.tenantActiveUsersSub')}
            color="#06b6d4"
            style={{ flex: '1 1 400px' }}
            headerExtra={tenantChartSelector}
            fullscreenContent={renderTenantUsersChart(480)}
          >
            {renderTenantUsersChart(240)}
          </ChartSection>
        </div>
      )}

      {/* 8. Activity heatmap */}
      <ChartSection
        title={t('insights.pulse.activityHeatmap')}
        subtitle={t('insights.pulse.activityHeatmapSub')}
        color="#ef4444"
        fullscreenWidth={1200}
      >
        <ActivityHeatmap
          agentTimeseries={agentTimeseries}
          period={periodDays}
          loading={loading}
        />
      </ChartSection>
    </div>
  );
}
